import type { StripePromoCodeDto, WorkspaceSubscriptionStatus } from '@/features/billing/types';

export function normalizePromoCode(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/** Stripe ignore la casse des codes promo. */
export function isSamePromoCode(
  left: string | null | undefined,
  right: string | null | undefined,
): boolean {
  const normalizedLeft = normalizePromoCode(left);
  const normalizedRight = normalizePromoCode(right);
  if (!normalizedLeft || !normalizedRight) return normalizedLeft === normalizedRight;
  return normalizedLeft.toLowerCase() === normalizedRight.toLowerCase();
}

/**
 * Code envoyé au paiement : celui validé dans le tunnel prime sur celui saisi à l'inscription.
 * Le back ne l'enregistre sur le workspace qu'une fois la souscription acceptée par Stripe.
 */
export function resolveCheckoutPromoCode(
  validatedCode: string | null | undefined,
  workspaceCode: string | null | undefined,
): string | null {
  return normalizePromoCode(validatedCode) ?? normalizePromoCode(workspaceCode);
}

/**
 * Le compte partenaire a son code imposé par le back. Un impayé porte sur une facture déjà émise
 * que le back ne remplace pas : seul un premier paiement en attente peut changer de code.
 */
export function canEditCheckoutPromoCode({
  isPartnerOffer,
  subscriptionStatus,
}: {
  isPartnerOffer: boolean;
  subscriptionStatus: WorkspaceSubscriptionStatus | null | undefined;
}): boolean {
  return !isPartnerOffer && subscriptionStatus !== 'PAST_DUE';
}

/**
 * La facture d'un premier paiement en attente est figée chez Stripe. Pour un autre code, le back
 * annule la souscription et en recrée une : il faut alors poster la création avec le nouveau code
 * au lieu de reprendre l'ancien paiement, et confirmer le secret qu'elle renvoie.
 */
export function shouldReplacePendingPromotion({
  currentStatus,
  requestedPromoCode,
  pendingPromoCode,
}: {
  currentStatus: WorkspaceSubscriptionStatus | null | undefined;
  requestedPromoCode: string | null | undefined;
  pendingPromoCode: string | null | undefined;
}): boolean {
  return (
    currentStatus === 'INCOMPLETE' &&
    normalizePromoCode(requestedPromoCode) !== null &&
    !isSamePromoCode(requestedPromoCode, pendingPromoCode)
  );
}

export type PromoCodeCheck =
  | { kind: 'accepted'; code: string }
  | { kind: 'notFound' }
  | { kind: 'inactive' }
  | { kind: 'failed'; message: string | null };

export function resolvePromoCodeCheck(
  promoCode: StripePromoCodeDto | null,
  error: { code?: string; message?: string } | null,
): PromoCodeCheck {
  if (promoCode) {
    return promoCode.active ? { kind: 'accepted', code: promoCode.code } : { kind: 'inactive' };
  }
  if (error?.code === 'NOT_FOUND' || error?.message === 'errors.notFound') {
    return { kind: 'notFound' };
  }
  return { kind: 'failed', message: error?.message ?? null };
}
